// @flow

import { get, isEmpty } from 'lodash';
import { useEffect, useReducer } from 'react';

import apolloStorage from 'app/common/apollo/storage';
import { GRAPHQL_API_URL } from 'app/common/apollo/constants';
import { createCacheKey } from 'app/common/apollo/utils';
import { postJson } from 'app/common/fetch';

import combineToLegacyCompatibleServices from './combineToLegacyCompatibleServices';

import type { Service } from 'app/service/types';

import type { ServiceLoaderResults } from './types';

type allServicesResponse = {
  data: {
    services: ?Array<?Service>,
  },
};

type allServicesVariables = {
  isPublic: boolean,
  isUpcoming?: boolean,
};

// This is set to always query for private services, but we could disable
// that conditionally if we knew on load whether a user has private services
// or not. Maybe in the future the backend can inform us there.
const SHOULD_LOAD_PRIVATE_SERVICES = true;

// The name of this query must match the name used by the cache here:
// https://github.com/zapier/zapier/blob/master/web/backend/zapier/api/resources_graphql.py#L52
const operationName = 'ThickServiceQuery';

const query = `
  query ${operationName}(
    $isPublic: Boolean!,
    $isUpcoming: Boolean,
  ) {
    services(isPublic: $isPublic, isUpcoming: $isUpcoming) {
      id
      ageInDays
      authType
      banner
      categories {
        id
        name
        slug
      }
      hasFilters
      hasReads
      hasSearches
      hasSearchesOrWrites
      hasUpfrontFields
      hasWrites
      isBeta
      isBuiltIn
      isDeprecated
      isFeatured
      isHidden
      isInvite
      isPremium
      isPublic
      isUpcoming
      name
      popularity
      slug
      __typename
    }
  }
`;

const publicServiceVariables = {
  isPublic: true,
  isUpcoming: false,
};

const privateServiceVariables = {
  isPublic: false,
};

const publicServicesCacheId = createCacheKey(
  operationName,
  publicServiceVariables
);
const privateServicesCacheId = createCacheKey(
  operationName,
  privateServiceVariables
);

let hasLoadedFromNetwork = false;

const loadQueryFromCache = async (cacheId: string) =>
  apolloStorage.getItem(cacheId) || [];

const saveToIndexedDB = (cacheId: string, result: any) =>
  apolloStorage.setItem(cacheId, result);

const graphqlRequest = async (variables: allServicesVariables) => {
  const result: allServicesResponse = await postJson(GRAPHQL_API_URL, {
    query,
    operationName,
    variables,
  });

  return get(result, ['data', 'services']) || [];
};

export type PublicAndPrivateServices = {
  publicServices: Array<Service>,
  privateServices: Array<Service>,
};

const loadServicesFromNetwork = async () => {
  const [publicServices, privateServices] = await Promise.all([
    graphqlRequest(publicServiceVariables),
    SHOULD_LOAD_PRIVATE_SERVICES ? graphqlRequest(privateServiceVariables) : [],
  ]);

  hasLoadedFromNetwork = true;

  saveToIndexedDB(publicServicesCacheId, publicServices);
  saveToIndexedDB(privateServicesCacheId, privateServices);

  return [publicServices, privateServices];
};

// Kick start the services request once this module is loaded. We perform this
// at module load rather than component load as the requests for public /
// private services is potentially slow, and we want it to be ready for the user
// when they need it. Some pages may inadvertantly kick this request off and
// not require it, but this is better than having a very editor which are
// waiting for these requests to return.
let servicesNetworkRequests;
// Super hacky, but needed:
// kick-starting the network request during module load prevents us from being able to mock this network requests
// using our `renderForIntegration` test util, because by the time we call it, this network request has already
// fired. So, we skip starting the network request here.
if (process.env.NODE_ENV !== 'test') {
  servicesNetworkRequests = loadServicesFromNetwork();
}

type State = {
  error: ?Error,
  loading: boolean,
  services: PublicAndPrivateServices,
};

type TypeOnlyAction = {
  type: 'loading' | 'load-fail',
};

type ActionWithPayload = {
  type: 'load-success',
  ...$Exact<PublicAndPrivateServices>,
};

type Action = TypeOnlyAction | ActionWithPayload;

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'loading':
      return {
        loading: true,
        error: null,
        services: state.services, // keep using previous services
      };
    case 'load-success':
      return {
        error: null,
        services: {
          publicServices: action.publicServices || [],
          privateServices: action.privateServices || [],
        },
        loading: false,
      };
    case 'load-fail':
      return {
        loading: false,
        error: new Error(
          'Unable to successfully load services, try again later.'
        ),
        services: state.services, // keep previous services
      };
    default:
      throw new Error(
        `Unexpeted reducer action type ${action.type} in useAllServicesLoader`
      );
  }
};

type Props = {
  // If `false` this will filter out private service results from the child.
  // It does not prevent the private service request from occuring.
  includePrivate: boolean,
};

// Heads up! This hook should be the only consumer of the `allServices`
// query. This query is expensive both from a frontend and backend perspective
// and should be used with great caution!
//
// One thing you will notice immediately is that this query happens outside of
// Apollo. We make a vanilla `fetch` request to our GraphQL endpoint.
// The reasoning for this is that this query responds with such a large amount
// of information that Apollo is sluggish when trying to normalize the data.
// See https://www.apollographql.com/docs/react/advanced/caching.html if you
// want to learn how Apollo caches its data.
//
// Secondly, this hook also kicks off its network request _outside_ the React component
// that uses the hook. We do this at the module level so that when this code is
// imported, it will begin making the `allServices` request.
// The `allServices` request can suffer from extremely high latency, so we want
// to ensure a solid UX for our users by making this request as early as we can,
// then when they finally need that data it _should_ be available to them.
const useAllServicesLoader = ({
  includePrivate,
}: Props): ServiceLoaderResults => {
  const initialState: State = {
    services: {
      publicServices: [],
      privateServices: [],
    },
    loading: !hasLoadedFromNetwork,
    error: null,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const onReceiveServices = ({ publicServices, privateServices }) => {
    dispatch({ type: 'load-success', publicServices, privateServices });
  };

  useEffect(() => {
    const loadServices = async () => {
      dispatch({ type: 'loading' });

      // If the promise previously failed and we are returning to this page
      // we should initialize a new promise so that the request is made once
      // again
      if (!servicesNetworkRequests) {
        servicesNetworkRequests = loadServicesFromNetwork();
      }

      // If we haven't loaded from network yet, then lets try pulling the data
      // out of IndexedDB and providing that to the child. We will perform this
      // in parallel with the network request, we always want the network to
      // request to win regardless if it comes first or second
      Promise.all([
        loadQueryFromCache(publicServicesCacheId),
        includePrivate ? loadQueryFromCache(privateServicesCacheId) : [],
      ])
        .then(([publicServices, privateServices]) => {
          // Only set this result if the network hasn't responded, Indexeddb cache
          // should always be considered stale when compared with the latest
          // network response, so we need to ensure we aren't going to overwrite
          // the network response
          if (
            !hasLoadedFromNetwork &&
            (!isEmpty(publicServices) || !isEmpty(privateServices))
          ) {
            onReceiveServices({ publicServices, privateServices });
          }
        })
        // Swallow any IndexedDB error that may occur as we will rely on the
        // network if IndexedDB fails
        .catch(console.error);

      try {
        const [publicServices, privateServices] = await servicesNetworkRequests;
        onReceiveServices({ publicServices, privateServices });
      } catch (e) {
        servicesNetworkRequests = null; // Clear the previous promise if it is rejected
        dispatch({ type: 'load-fail' });
      }
    };

    loadServices();
  }, [includePrivate]);

  const { publicServices, privateServices } = state.services;

  const legacyCompatibleServices = combineToLegacyCompatibleServices(
    publicServices,
    privateServices,
    includePrivate
  );

  return {
    error: state.error,
    loading: state.loading,
    services: legacyCompatibleServices,
  };
};

export default useAllServicesLoader;
